import React, { useEffect, useMemo, useState } from 'react';
import { ICdwTableFieldsFragment } from '../../../../utils/apollo/graphqlSchema';
import {
  AddTableFormWrapper,
  ConfirmCancelButtonsWrapper,
  FormRowActionButtons,
  SelectTableTypeWrapper,
} from '../DataModelerCommonUI';
import SelectCdwTableForm from '../SelectCdwTableForm';
import { PrimaryButton, SecondaryButton } from '../../../../components/Button';
import { generateUniqueDataModelName } from '../../../../redux/slices/dataModeler/utils';
import { FieldLabel } from '../../../../components/Form';
import RadioGroup from '../../../../components/RadioGroup';
import { IAddTable, useDataModelerContext } from '../../DataModelerContext';
import { assertNotNil } from '../../../../utils/types';

type TableType = 'Fact' | 'Dimension';

interface IProps {
  closeAddTableForm: () => void;
}

const AddTableForm = ({ closeAddTableForm }: IProps) => {
  const {
    schemaObj,
    draftEdits,
    addedViewKeys,
    addedTableKeys,
    handleAddTable,
  } = useDataModelerContext();

  const forceFact =
    schemaObj?.fact.tableKeys.length === 0 &&
    !draftEdits?.addedFactsData?.length;

  const [searchQuery, setSearchQuery] = useState('');
  const [tableType, setTableType] = useState<TableType>(
    forceFact ? 'Fact' : 'Dimension',
  );

  const [selectedTable, setSelectedTable] =
    useState<ICdwTableFieldsFragment | null>(null);

  const allViewKeys: string[] = useMemo(() => {
    assertNotNil(schemaObj);
    let ret: string[] = [];
    ret.push(schemaObj.fact.key);
    ret = [...ret, ...schemaObj.dimensions.map((dim) => dim.key)];
    return [...ret, ...addedViewKeys];
  }, [addedViewKeys, schemaObj]);

  const allTableKeys: string[] = useMemo(() => {
    assertNotNil(schemaObj);
    let ret: string[] = [];
    ret = [...ret, ...schemaObj.tables.map((tbl) => tbl.key)];
    return [...ret, ...addedTableKeys];
  }, [addedTableKeys, schemaObj]);

  const handleConfirmDimension = (table: ICdwTableFieldsFragment) => {
    const desiredViewKey = table.shortName.toUpperCase();
    const viewKey = generateUniqueDataModelName(
      desiredViewKey,
      null,
      allViewKeys,
    );
    const tableKey = generateUniqueDataModelName(
      `${schemaObj?.key}.${viewKey}`,
      null,
      allTableKeys,
    );
    handleAddTable({
      viewKey,
      tableKey,
      sqlTableName: table.sqlName,
      isDimension: true,
    } satisfies IAddTable);
    closeAddTableForm();
  };

  const handleConfirmFact = (table: ICdwTableFieldsFragment) => {
    assertNotNil(schemaObj);
    const viewKey = schemaObj.fact.key;
    const desiredShortTableKey = table.shortName.toUpperCase();
    const schemaKeyPrefix = `${schemaObj.key}.`;
    const allShortTableKeys = allTableKeys.map((s) =>
      s.startsWith(schemaKeyPrefix) ? s.substring(schemaKeyPrefix.length) : s,
    );
    const uniqueShortTableKey = generateUniqueDataModelName(
      desiredShortTableKey,
      null,
      allShortTableKeys,
    );
    const desiredTableKey = `${schemaObj.key}.${uniqueShortTableKey}`;
    // this should already be unique, but just in case ...
    const tableKey = generateUniqueDataModelName(
      desiredTableKey,
      null,
      allTableKeys,
    );
    handleAddTable({
      viewKey,
      tableKey,
      sqlTableName: table.sqlName,
      isDimension: false,
    } satisfies IAddTable);
    closeAddTableForm();
  };

  const handleTypeChange = (newType: TableType) => {
    if (forceFact && newType === 'Dimension') {
      return;
    }
    setTableType(newType);
  };

  const handleConfirm = () => {
    if (!selectedTable) {
      return;
    }
    if (tableType === 'Dimension') {
      handleConfirmDimension(selectedTable);
    } else {
      handleConfirmFact(selectedTable);
    }
    setSelectedTable(null);
    setSearchQuery('');
  };

  const handleClose = () => {
    setSearchQuery('');
    closeAddTableForm();
  };

  const tableTypeOptions = useMemo(
    () => [
      { label: 'Fact', value: 'Fact' },
      { label: 'Dimension', value: 'Dimension', disabled: forceFact },
    ],
    [forceFact],
  );

  // Close on unmount
  useEffect(() => {
    return () => {
      closeAddTableForm();
    };
  }, [closeAddTableForm]);

  return (
    <AddTableFormWrapper>
      <SelectCdwTableForm
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedTable={selectedTable}
        onSelectTable={setSelectedTable}
      />
      <FormRowActionButtons>
        <FieldLabel>Table Type</FieldLabel>
        <SelectTableTypeWrapper>
          <RadioGroup
            value={tableType}
            options={tableTypeOptions}
            name={''}
            onChange={(s) => handleTypeChange(s as TableType)}
          />
        </SelectTableTypeWrapper>
        <ConfirmCancelButtonsWrapper>
          <PrimaryButton
            disabled={!selectedTable}
            onClick={handleConfirm}
            trackContext="data modeler"
            trackAction="add table - confirm"
          >
            Confirm
          </PrimaryButton>
          <SecondaryButton
            onClick={handleClose}
            trackContext="data modeler"
            trackAction="add table - cancel"
          >
            Cancel
          </SecondaryButton>
        </ConfirmCancelButtonsWrapper>
      </FormRowActionButtons>
    </AddTableFormWrapper>
  );
};

export default AddTableForm;
